// apps/cli/src/lib/bootstrap-aci.ts
import { runAz } from "./az.js";

export interface GitHubAppCreds {
  appId: string;
  slug: string;
  org: string;
  installationId: string;
  /** base64-encoded PEM — treated as a secret, passed as a secure env var. */
  pemB64: string;
}

export interface AciSpec {
  aciName: string;
  resourceGroup: string;
  location: string;
  subscriptionId: string;
  miResourceId: string;
  miClientId: string;
  appRegClientId: string;
  image: string;
  gatewayImageRef?: string;
  foundryTracing?: string;
  /**
   * Release-channel manifest URL. Threaded to the install so the deployed gateway
   * and the updater job poll the same channel. Absent ⇒ both fall back to their
   * baked default channel.
   */
  updateChannelUrl?: string;
  githubApp?: GitHubAppCreds;
  /**
   * The platform release this install IS (bare X.Y.Z).
   *
   * The engine bakes its own answer at image-build time, and under publish→select
   * that answer is at least one declaration old: the image a release selects was
   * built before the release declared itself. This CLI, by contrast, is built from
   * the tree that carries the declaration, so it can simply say. Absent ⇒ the
   * engine falls back to its baked value, which is the documented behaviour for
   * launchers that pass nothing, not an error.
   */
  platformVersion?: string;
  /** Install without brain repos or the brain-backed executors. Test/CI rigs only. */
  skipBrains?: boolean;
  /**
   * The founder's AAD object id — who to grant the platform's data-plane roles to.
   *
   * The installer holds Owner at subscription scope and already self-grants the
   * exact two roles a human needs (`Foundry User`, and a Key Vault secrets role)
   * at step 3b. The ONLY thing it has never known is who the founder is, which is
   * why granting them was pushed out to a command the founder ran afterwards.
   * This is that missing ingredient, and it is non-secret (an object id, not a
   * credential), so it belongs in the plain env block.
   *
   * Absent ⇒ the installer skips the founder grant entirely and the founder falls
   * back to `m8t prereqs --fix`. Never a reason to fail an install.
   */
  founderObjectId?: string;
  /**
   * Opt-in registration details, threaded only when the operator passed the
   * matching flag. Non-secret registration data, not credentials, so they
   * belong in the plain env block (not --secure-environment-variables).
   * Absent ⇒ the installer enrolls pseudonymously.
   */
  enrollContactEmail?: string;
  enrollCompany?: string;
}

/**
 * Build the `az container create` args (MI attach + the runtime env).
 *
 * Uses `--restart-policy OnFailure` so a transient first-command death (e.g. MI
 * propagation lag before `login_with_retry` was introduced) triggers a container
 * restart and self-heals. The entrypoint is idempotent (hydrate_status restores
 * prior progress on restart). `fail()` exits 0 after writing the `failed` status
 * blob — the blob is the source of truth, so an honest failure stops ACI from
 * restart-cycling indefinitely.
 */
export function buildAciCreateArgs(s: AciSpec): string[] {
  // Plain (non-secret) environment variables — one --environment-variables block.
  const env = [
    `SUBSCRIPTION_ID=${s.subscriptionId}`,
    `RESOURCE_GROUP=${s.resourceGroup}`,
    `LOCATION=${s.location}`,
    `MI_CLIENT_ID=${s.miClientId}`,
    `APP_REG_CLIENT_ID=${s.appRegClientId}`,
    // The installer's own image ref, so the install can provision the updater job
    // to run THIS engine. Derived from the launched image rather than threaded
    // separately, so the two can never disagree — and never read from the release
    // pin, which can name an installer several versions behind the running one.
    `INSTALLER_IMAGE_REF=${s.image}`,
  ];
  if (s.platformVersion) env.push(`M8T_PLATFORM_VERSION=${s.platformVersion}`);
  if (s.gatewayImageRef) env.push(`GATEWAY_IMAGE_REF=${s.gatewayImageRef}`);
  if (s.foundryTracing) env.push(`FOUNDRY_TRACING=${s.foundryTracing}`);
  if (s.skipBrains) env.push("SKIP_BRAINS=true");
  if (s.founderObjectId) env.push(`FOUNDER_OBJECT_ID=${s.founderObjectId}`);
  if (s.updateChannelUrl) env.push(`M8T_UPDATE_CHANNEL_URL=${s.updateChannelUrl}`);
  if (s.enrollContactEmail) env.push(`M8T_ENROLL_CONTACT_EMAIL=${s.enrollContactEmail}`);
  if (s.enrollCompany) env.push(`M8T_ENROLL_COMPANY=${s.enrollCompany}`);
  if (s.githubApp) {
    // Non-secret GitHub App metadata is safe in the plain env block.
    const g = s.githubApp;
    env.push(
      `GITHUB_ORG=${g.org}`,
      `GH_APP_ID=${g.appId}`,
      `GH_APP_SLUG=${g.slug}`,
      `GH_INSTALLATION_ID=${g.installationId}`,
    );
  }

  const args = [
    "container", "create",
    "--name", s.aciName,
    "--resource-group", s.resourceGroup,
    "--location", s.location,
    "--subscription", s.subscriptionId,
    "--image", s.image,
    "--os-type", "Linux",
    "--restart-policy", "OnFailure",
    "--assign-identity", s.miResourceId,
    "--cpu", "1", "--memory", "1.5",
    "--environment-variables", ...env,
  ];

  if (s.githubApp) {
    // The PEM is a secret — pass it via --secure-environment-variables so az
    // CLI redacts it from logs and the ACI properties API response.
    args.push("--secure-environment-variables", `GH_APP_PEM_B64=${s.githubApp.pemB64}`);
  }

  args.push("--only-show-errors");
  return args;
}

export async function kickInstaller(s: AciSpec): Promise<void> {
  await runAz(buildAciCreateArgs(s));
}
